import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';

const TemplateCreateModal = ({ isOpen, onClose, template = null }) => {
  const [formData, setFormData] = useState({
    name: '',
    trigger_rules: '',
    hpi_template: '',
    ros_template: '',
    physical_exam_template: '',
    assessment_plan_template: '',
    after_visit_summary_template: '',
    additional_mdm_template: ''
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  const isEditing = template !== null;

  // Load template data when editing
  useEffect(() => {
    if (template) {
      setFormData({
        name: template.name || '',
        trigger_rules: template.trigger_rules || '',
        hpi_template: template.hpi_template || '',
        ros_template: template.ros_template || '',
        physical_exam_template: template.physical_exam_template || '',
        assessment_plan_template: template.assessment_plan_template || '',
        after_visit_summary_template: template.after_visit_summary_template || '',
        additional_mdm_template: template.additional_mdm_template || ''
      });
    } else {
      setFormData({
        name: '',
        trigger_rules: '',
        hpi_template: '',
        ros_template: '',
        physical_exam_template: '',
        assessment_plan_template: '',
        after_visit_summary_template: '',
        additional_mdm_template: ''
      });
    }
    setError('');
  }, [template]);

  const handleChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      setError('Template name is required');
      return;
    }

    setIsSaving(true);
    setError('');

    try {
      const url = isEditing
        ? `/api/user/templates/${template._id}`
        : '/api/user/templates';

      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to save template');
      }

      onClose();
    } catch (err) {
      setError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center items-center p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-800">
            {isEditing ? 'Edit Template' : 'Create New Template'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            disabled={isSaving}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
                {error}
              </div>
            )}

            {/* Template Name */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Template Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => handleChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="e.g., Routine Annual Physical"
              />
            </div>

            {/* Trigger Rules */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Rules to Trigger Template
              </label>
              <textarea
                value={formData.trigger_rules}
                onChange={(e) => handleChange('trigger_rules', e.target.value)}
                rows={3}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Describe when this template should be automatically applied (e.g., 'Patient mentions annual checkup or routine physical')"
              />
              <p className="mt-1 text-xs text-gray-500">
                Used for automatic template selection when enabled in settings.
              </p>
            </div>

            {/* HPI Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                HPI Template
              </label>
              <textarea
                value={formData.hpi_template}
                onChange={(e) => handleChange('hpi_template', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Enter HPI template structure and instructions..."
              />
            </div>

            {/* ROS Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Review of Systems Template
              </label>
              <textarea
                value={formData.ros_template}
                onChange={(e) => handleChange('ros_template', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Enter ROS template structure and instructions..."
              />
            </div>

            {/* Physical Exam Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Physical Exam Template
              </label>
              <textarea
                value={formData.physical_exam_template}
                onChange={(e) => handleChange('physical_exam_template', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Enter Physical Exam template structure and instructions..."
              />
            </div>

            {/* Assessment & Plan Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Assessment & Plan Template
              </label>
              <textarea
                value={formData.assessment_plan_template}
                onChange={(e) => handleChange('assessment_plan_template', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Enter Assessment & Plan template structure and instructions..."
              />
            </div>

            {/* Patient Instructions Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Patient Instructions Template
              </label>
              <textarea
                value={formData.after_visit_summary_template}
                onChange={(e) => handleChange('after_visit_summary_template', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Enter Patient Instructions template structure and instructions..."
              />
            </div>

            {/* Additional MDM Template */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Additional MDM Template
              </label>
              <textarea
                value={formData.additional_mdm_template}
                onChange={(e) => handleChange('additional_mdm_template', e.target.value)}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                placeholder="Enter Additional MDM template structure and instructions..."
              />
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="px-4 py-2 text-white bg-teal-600 rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Create Template'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TemplateCreateModal;
